import { useCallback, useEffect, useRef, useState } from "react";
import { http, formatApiError, API } from "../lib/api";
import { shortenFileName } from "../lib/utils";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  FileArrowUp, DownloadSimple, Trash, Package, CheckCircle, Image as ImageIcon, FileText, UserCircle,
} from "@phosphor-icons/react";

const IMG_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]);
const PDF_EXTS = new Set(["pdf"]);

function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function FileIcon({ ext, size = 16 }) {
  const e = (ext || "").toLowerCase();
  if (IMG_EXTS.has(e)) return <ImageIcon size={size} weight="bold" className="text-blue-600" />;
  if (PDF_EXTS.has(e)) return <FileText size={size} weight="bold" className="text-red-600" />;
  return <FileText size={size} weight="bold" className="text-zinc-500" />;
}

/**
 * Compact deliverable uploader scoped to a single task.
 * Renders a trigger button; opens a dialog that shows the task's existing deliverables
 * and lets you attach more (auto-linked via task_id).
 */
export default function TaskDeliverableUpload({ task, canDelete = false, className = "",
                                                open: openProp, onOpenChange, hideTrigger = false }) {
  // Support both uncontrolled (internal) and controlled (parent) `open` state
  // so an outside pill can programmatically open this dialog.
  const [openInner, setOpenInner] = useState(false);
  const open = openProp !== undefined ? openProp : openInner;
  const setOpen = (v) => {
    if (onOpenChange) onOpenChange(v);
    if (openProp === undefined) setOpenInner(v);
  };
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!task?.project) return;
    try {
      const { data } = await http.get(`/projects/${encodeURIComponent(task.project)}/deliverables`);
      const scoped = data.filter((d) => d.task_id === task.id);
      setItems(scoped);
      setCount(scoped.length);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [task?.project, task?.id]);

  // Preload count once so we can show it on the button badge without opening the dialog
  useEffect(() => { load(); }, [load]);

  // Refresh whenever the dialog opens
  useEffect(() => { if (open) load(); }, [open, load]);

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", caption);
      form.append("task_id", task.id);
      await http.post(`/projects/${encodeURIComponent(task.project)}/deliverables`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Deliverable uploaded");
      setCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await http.delete(`/deliverables/${pendingDelete.id}`);
      toast.success("Deleted");
      setPendingDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      {!hideTrigger && (
        <button
          type="button"
          data-testid={`task-deliverable-btn-${task.id}`}
          onClick={() => setOpen(true)}
          className={`rounded-none h-8 flex items-center gap-1.5 border border-emerald-700 bg-emerald-50 text-emerald-800 hover:bg-emerald-700 hover:text-white px-3 text-xs font-mono uppercase tracking-widest ${className}`}
        >
          <Package size={12} weight="bold" /> Deliverable
          {count > 0 && (
            <span data-testid={`task-deliverable-count-${task.id}`}
                  className="ml-1 bg-emerald-700 text-white group-hover:bg-white group-hover:text-emerald-700 px-1.5 py-0.5 label-overline">
              {count}
            </span>
          )}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-xl max-h-[90vh] overflow-y-auto"
                       data-testid={`task-deliverable-dialog-${task.id}`}>
          <DialogHeader>
            <div className="label-overline text-emerald-700 mb-1 flex items-center gap-1.5">
              <CheckCircle size={12} weight="bold" /> Task Deliverable
            </div>
            <DialogTitle className="font-display text-xl font-black tracking-tighter uppercase">
              {task.title}
            </DialogTitle>
            <div className="label-overline text-zinc-500 mt-1">Project · {task.project}</div>
          </DialogHeader>

          <div className="mt-3 border border-emerald-400 bg-emerald-50/60 p-3">
            <div className="label-overline text-emerald-800 mb-2">Attach a new file</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid={`task-deliverable-caption-${task.id}`}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Optional caption (e.g. v1 concept, revised layout)"
                className="rounded-none border-zinc-400 focus-visible:ring-emerald-600 focus-visible:ring-offset-0 h-9 text-xs"
              />
              <Button
                type="button"
                disabled={uploading}
                onClick={pick}
                data-testid={`task-deliverable-upload-${task.id}`}
                className="rounded-none h-9 bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs px-4 shrink-0"
              >
                <FileArrowUp size={12} weight="bold" className="mr-1.5" />
                {uploading ? "Uploading…" : "Choose File"}
              </Button>
              <input ref={fileRef} type="file" hidden onChange={upload} />
            </div>
            <div className="text-[10px] text-emerald-700 mt-2 font-mono">
              File will be auto-linked to this task and visible under the project&apos;s Deliverables and the Client Portal.
            </div>
          </div>

          <div className="mt-4 border border-zinc-300" data-testid={`task-deliverable-list-${task.id}`}>
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-300 flex items-center gap-2">
              <Package size={12} weight="bold" className="text-zinc-600" />
              <span className="label-overline text-zinc-800">Attached Files</span>
              <span className="ml-auto label-overline text-zinc-500">{items.length}</span>
            </div>

            {items.length === 0 && (
              <div className="p-6 text-center label-overline text-zinc-400"
                   data-testid={`task-deliverable-empty-${task.id}`}>
                No files attached yet
              </div>
            )}

            {items.map((d) => (
              <div key={d.id} data-testid={`task-deliverable-row-${d.id}`}
                   className="px-3 py-2.5 border-b border-zinc-200 last:border-b-0 flex items-center gap-3">
                <div className="shrink-0 w-8 h-8 border border-zinc-300 bg-zinc-50 flex items-center justify-center">
                  <FileIcon ext={d.ext} size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900 truncate" title={d.name}>
                    {shortenFileName(d.name, 34)}
                  </div>
                  {d.caption && (
                    <div className="text-xs text-zinc-600 truncate">{d.caption}</div>
                  )}
                  <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1.5 flex-wrap mt-0.5">
                    <UserCircle size={9} weight="bold" /> {d.uploaded_by_name}
                    <span>·</span>
                    <span>{bytes(d.size || 0)}</span>
                    {d.created_at && (
                      <>
                        <span>·</span>
                        <span>{new Date(d.created_at).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                </div>
                <a
                  href={`${API}/deliverables/${d.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`task-deliverable-download-${d.id}`}
                  className="label-overline text-zinc-600 hover:text-blue-700 px-1"
                  aria-label="Download"
                >
                  <DownloadSimple size={13} weight="bold" />
                </a>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => setPendingDelete(d)}
                    data-testid={`task-deliverable-delete-${d.id}`}
                    className="text-zinc-400 hover:text-red-600 p-1"
                    aria-label="Delete"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-3 border-t border-zinc-300 mt-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Destructive Action</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase break-all">
              Delete &quot;{shortenFileName(pendingDelete?.name, 40)}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will permanently remove the file. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-task-deliverable-delete"
              className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-task-deliverable-delete" onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs">
              Yes, Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
